
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useConnection } from './ConnectionContext';
import { 
  initDB, 
  queueOperation as queueOfflineOperation, 
  getCachedData as getOfflineCachedData, 
  syncPendingOperations,
  clearAllCaches
} from '@/utils/offlineStorage';

interface PendingOperation {
  id: string;
  table: string;
  operation: 'insert' | 'update' | 'delete';
  data: any;
  timestamp: Date;
}

interface OfflineContextType {
  isInitialized: boolean;
  syncStatus: 'idle' | 'syncing' | 'error';
  lastSyncTime: Date | null;
  syncData: () => Promise<void>;
  queueOperation: (operation: PendingOperation) => Promise<void>;
  getCachedData: (table: string) => Promise<any[]>;
  clearCache: () => Promise<void>;
}

const OfflineContext = createContext<OfflineContextType | undefined>(undefined);

interface OfflineProviderProps {
  children: React.ReactNode;
}

export const OfflineProvider: React.FC<OfflineProviderProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [syncStatus, setSyncStatus] = useState<OfflineContextType['syncStatus']>('idle');
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const { isOnline } = useConnection();
  const { toast } = useToast();

  useEffect(() => {
    const initializeOfflineStorage = async () => {
      try {
        await initDB();
        setIsInitialized(true);
        console.log('Offline storage initialized successfully');
      } catch (error) {
        console.error('Error initializing offline storage:', error);
        toast({
          title: "Offline Storage Error",
          description: "Failed to initialize offline storage. Some features may not work.",
          variant: "destructive"
        });
      }
    };

    initializeOfflineStorage();
  }, [toast]);

  const syncData = useCallback(async () => {
    if (!isOnline) {
      console.log('Skipping sync because the app is offline');
      return;
    }

    setSyncStatus('syncing');
    try {
      // Sync pending operations using the utility function
      await syncPendingOperations();

      // Refresh all data from Supabase
      const tablesToSync = ['academic_years', 'community_members', 'profiles', 'schools', 'students', 'terms', 'visits'];
      for (const table of tablesToSync) {
        try {
          const { data, error } = await supabase.from(table).select('*');
          if (error) {
            console.error(`Error fetching ${table} from Supabase:`, error);
            throw error;
          }
          
          // Cache the data using the utility function
          const { cacheTableData } = await import('@/utils/offlineStorage');
          await cacheTableData(table as any, data);
          console.log(`Table ${table} synced successfully`);
        } catch (fetchError: any) {
          console.error(`Error syncing table ${table}:`, fetchError);
          toast({
            title: "Sync Error",
            description: `Failed to sync table ${table}: ${fetchError.message}`,
            variant: "destructive"
          });
          setSyncStatus('error');
          return;
        }
      }

      setSyncStatus('idle');
      setLastSyncTime(new Date());
      toast({
        title: "Sync Complete",
        description: "Offline data is now up to date.",
      });
    } catch (error: any) {
      console.error('Sync failed:', error);
      setSyncStatus('error');
      toast({
        title: "Sync Error",
        description: `Data synchronization failed: ${error.message}`,
        variant: "destructive"
      });
    }
  }, [isOnline, toast]);

  const queueOperation = async (operation: PendingOperation) => {
    try {
      // Map the operation format to match the utility function
      const mappedOperation = {
        tableName: operation.table as any,
        type: operation.operation === 'insert' ? 'create' as const : 
              operation.operation === 'update' ? 'update' as const : 
              'delete' as const,
        data: operation.data
      };

      await queueOfflineOperation(
        mappedOperation.tableName,
        mappedOperation.type,
        mappedOperation.data
      );
      
      console.log(`Operation queued successfully for ${operation.table}`);
    } catch (error) {
      console.error('Error queueing operation:', error);
      toast({
        title: "Queue Error",
        description: "Failed to queue operation for offline sync.",
        variant: "destructive"
      });
    }
  };

  const getCachedData = async (table: string): Promise<any[]> => {
    try {
      return await getOfflineCachedData(table as any);
    } catch (error) {
      console.error(`Error getting cached data for ${table}:`, error);
      toast({
        title: "Cache Error",
        description: `Failed to retrieve cached data for ${table}.`,
        variant: "destructive"
      });
      return [];
    }
  };

  const clearCache = async () => {
    try {
      await clearAllCaches();
      console.log('Cache cleared successfully');
      toast({
        title: "Cache Cleared",
        description: "Local data cache has been cleared.",
      });
    } catch (error) {
      console.error('Error clearing cache:', error);
      toast({
        title: "Cache Error",
        description: "Failed to clear local data cache.",
        variant: "destructive"
      });
    }
  };

  const value: OfflineContextType = {
    isInitialized,
    syncStatus,
    lastSyncTime,
    syncData,
    queueOperation,
    getCachedData,
    clearCache,
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (context === undefined) {
    throw new Error("useOffline must be used within an OfflineProvider");
  }
  return context;
};
